import React, { useState } from "react";
import { Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Badge,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Quiz as QuizIcon,
  BarChart as StatsIcon,
  Logout as LogoutIcon,
  Feedback,
  ContactSupport,
  Settings as SettingsIcon,
  CalendarMonth as CalendarIcon,
  Lightbulb as LightbulbIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Notifications as NotificationsIcon,
  TerminalOutlined as TerminalOutlinedIcon,
  OpenInNew as OpenInNewIcon,
  FilterAlt as FilterAltIcon,
  Create as CreateIcon,
  FactCheckOutlined as FactCheckOutlinedIcon,
  GradingOutlined as GradingOutlinedIcon,
} from "@mui/icons-material";
import { Chip } from "@mui/material";

import { useAuth } from "../contexts/AuthContext";
import { useUnreadCounts } from "../hooks/useUnreadCounts";
import DashboardHome from "./DashboardHome";
import TopicList from "./TopicList";
import TopicForm from "./TopicForm";
import BulkAdd from "./BulkAdd";
import StatsPage from "./StatsPage";
import UserFeedbackList from "./feedback/UserFeedbackList";
import InquiryList from "./feedback/InquiryList";
import TopicRequestList from "./feedback/TopicRequestList";
import EvaluationReportList from "./feedback/EvaluationReportList";
import AppVersionSettings from "./settings/AppVersionSettings";
import WeeklyTopicsSettings from "./settings/WeeklyTopicsSettings";
import AdminUserManagement from "./settings/AdminUserManagement";
import PushNotificationSettings from "./settings/PushNotificationSettings";
import XPostUrlSettings from "./marketing/XPostUrlSettings";
import EvaluationList from "./evaluations/EvaluationList";
import PromptPreview from "./evaluations/PromptPreview";
import RubricList from "./rubrics/RubricList";
import firebaseSettings from "../emulatorConfig";

const drawerWidth = 240;

// 環境判定
const isEmulator = firebaseSettings.isEmulator;

interface NavigationItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  section?: string;
  roles?: ("admin" | "refiner")[];
  externalUrl?: string;
}

const navigationItems: NavigationItem[] = [
  {
    text: "ダッシュボード",
    icon: <DashboardIcon />,
    path: "/dashboard",
    roles: ["admin", "refiner"],
  },

  // 問題管理セクション
  {
    text: "問題一覧",
    icon: <QuizIcon />,
    path: "/opinions",
    section: "問題管理",
    roles: ["admin", "refiner"],
  },
  {
    text: "週間問題設定",
    icon: <CalendarIcon />,
    path: "/settings/weekly-topics",
    section: "問題管理",
    roles: ["admin"],
  },
  {
    text: "問題登録状況",
    icon: <StatsIcon />,
    path: "/stats",
    section: "問題管理",
    roles: ["admin"],
  },

  // データ分析セクション
  {
    text: "評価結果一覧",
    icon: <AssessmentIcon />,
    path: "/evaluations",
    section: "データ分析",
    roles: ["admin"],
  },
  {
    text: "プロンプト確認",
    icon: <TerminalOutlinedIcon />,
    path: "/prompt-preview",
    section: "データ分析",
    roles: ["admin"],
  },
  {
    text: "採点ルーブリック",
    icon: <FactCheckOutlinedIcon />,
    path: "/rubrics",
    section: "データ分析",
    roles: ["admin"],
  },

  // マーケティングセクション
  {
    text: "X投稿URL設定",
    icon: <CreateIcon />,
    path: "/marketing/x-post-url-settings",
    section: "マーケティング",
    roles: ["admin"],
  },
  {
    text: "ファネル分析",
    icon: <FilterAltIcon />,
    path: "/marketing/funnel",
    section: "マーケティング",
    roles: ["admin"],
    externalUrl:
      "https://analytics.google.com/analytics/web/?hl=ja#/analysis/a316437323p501962050/edit/J-DbM4l7S5upIQ4O7RKcCw",
  },
  {
    text: "Search Console",
    icon: <OpenInNewIcon />,
    path: "/marketing/search-console",
    section: "マーケティング",
    roles: ["admin"],
    externalUrl: "https://search.google.com/search-console?resource_id=sc-domain%3Aripuren.com",
  },
  // フィードバック管理セクション
  {
    text: "ご意見・ご要望",
    icon: <Feedback />,
    path: "/user-feedback",
    section: "カスタマーサクセス",
    roles: ["admin", "refiner"],
  },
  {
    text: "評価結果フィードバック",
    icon: <GradingOutlinedIcon />,
    path: "/evaluation-reports",
    section: "カスタマーサクセス",
    roles: ["admin", "refiner"],
  },
  {
    text: "お題リクエスト",
    icon: <LightbulbIcon />,
    path: "/topic-requests",
    section: "カスタマーサクセス",
    roles: ["admin", "refiner"],
  },
  {
    text: "お問い合わせ",
    icon: <ContactSupport />,
    path: "/inquiries",
    section: "カスタマーサクセス",
    roles: ["admin", "refiner"],
  },

  // 設定セクション
  {
    text: "アプリ設定",
    icon: <SettingsIcon />,
    path: "/settings/app-version",
    section: "設定",
    roles: ["admin"],
  },
  {
    text: "プッシュ通知",
    icon: <NotificationsIcon />,
    path: "/settings/push-notifications",
    section: "設定",
    roles: ["admin"],
  },
  {
    text: "管理者管理",
    icon: <PeopleIcon />,
    path: "/settings/admin-users",
    section: "設定",
    roles: ["admin"],
  },
];

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout, role } = useAuth();
  const location = useLocation();
  const { userFeedbackCount, inquiriesCount, topicRequestsCount, evaluationReportCount } =
    useUnreadCounts();

  // ロール別にメニューをフィルタリング
  const filteredNavigationItems = navigationItems.filter((item) => {
    if (!item.roles) return true; // roles未指定は全ロールに表示
    if (!role) return false; // ロールなしは非表示
    return item.roles.includes(role);
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const drawer = (
    <div>
      <Toolbar sx={{ flexDirection: "column", alignItems: "flex-start", py: 2 }}>
        <Typography variant="h6" noWrap component="div">
          リプレン管理
        </Typography>
        {isEmulator && (
          <Chip label="開発環境" color="warning" size="small" sx={{ mt: 1, fontSize: "0.75rem" }} />
        )}
      </Toolbar>
      <Divider />
      <List>
        {(() => {
          let currentSection: string | undefined = undefined;
          return filteredNavigationItems.map((item, index) => {
            const showSection = item.section && item.section !== currentSection;
            if (item.section) {
              currentSection = item.section;
            }

            // 未読件数を取得
            let badgeCount = 0;
            if (item.path === "/user-feedback") {
              badgeCount = userFeedbackCount;
            } else if (item.path === "/evaluation-reports") {
              badgeCount = evaluationReportCount;
            } else if (item.path === "/topic-requests") {
              badgeCount = topicRequestsCount;
            } else if (item.path === "/inquiries") {
              badgeCount = inquiriesCount;
            }

            return (
              <React.Fragment key={item.text}>
                {showSection && (
                  <>
                    {index > 0 && <Divider sx={{ my: 1 }} />}
                    <ListItem>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ pl: 2, fontWeight: 600 }}
                      >
                        {item.section}
                      </Typography>
                    </ListItem>
                  </>
                )}
                <ListItem disablePadding>
                  {item.externalUrl ? (
                    <ListItemButton
                      component="a"
                      href={item.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                      <OpenInNewIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    </ListItemButton>
                  ) : (
                    <ListItemButton
                      component={Link}
                      to={item.path}
                      selected={location.pathname === item.path}
                    >
                      <ListItemIcon>
                        <Badge badgeContent={badgeCount} color="error">
                          {item.icon}
                        </Badge>
                      </ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  )}
                </ListItem>
              </React.Fragment>
            );
          });
        })()}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: isEmulator ? "#ff9800" : undefined, // エミュレーター時はオレンジ
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              fontSize: { xs: "1rem", sm: "1.25rem" },
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {navigationItems.find((item) => item.path === location.pathname)?.text ||
              "リプレン管理画面"}
            {isEmulator && (
              <Chip
                label="開発"
                size="small"
                sx={{
                  ml: 1,
                  height: 20,
                  fontSize: "0.7rem",
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "inherit",
                }}
              />
            )}
          </Typography>
          <Typography
            variant="body2"
            sx={{ mr: 2, display: { xs: "none", md: "block" }, flexShrink: 0 }}
          >
            {user?.email}
          </Typography>
          <IconButton
            color="inherit"
            onClick={handleLogout}
            aria-label="ログアウト"
            sx={{ flexShrink: 0 }}
          >
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        {/* モバイル用ドロワー */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>

        {/* デスクトップ用ドロワー */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          maxWidth: "100%",
          overflowX: "hidden",
        }}
      >
        <Toolbar />
        <Routes>
          <Route path="/dashboard" element={<DashboardHome />} />
          <Route path="/opinions" element={<TopicList />} />
          <Route path="/opinions/new" element={<TopicForm />} />
          <Route path="/opinions/edit/:id" element={<TopicForm />} />
          <Route path="/stats" element={<StatsPage />} />
          <Route path="/user-feedback" element={<UserFeedbackList />} />
          <Route path="/evaluation-reports" element={<EvaluationReportList />} />
          <Route path="/topic-requests" element={<TopicRequestList />} />
          <Route path="/inquiries" element={<InquiryList />} />
          <Route path="/settings/weekly-topics" element={<WeeklyTopicsSettings />} />
          <Route path="/settings/app-version" element={<AppVersionSettings />} />
          <Route path="/settings/admin-users" element={<AdminUserManagement />} />
          <Route path="/settings/push-notifications" element={<PushNotificationSettings />} />
          <Route path="/marketing/x-post-url-settings" element={<XPostUrlSettings />} />
          <Route path="/evaluations" element={<EvaluationList />} />
          <Route path="/prompt-preview" element={<PromptPreview />} />
          <Route path="/rubrics" element={<RubricList />} />
          <Route path="/bulk-add" element={<BulkAdd />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default Layout;
